import type { GetApiShipmentsData } from '@/api';
import type { TableSort } from '@/ui';

import { formatSort, isSameSort, parseSort } from '../../../lib/tableSort';

/*
 * Состояние «Плана отгрузок» в search params маршрута. Период - как переключатель
 * макета «Неделя / Месяц / Всё»: ограничивает плановую дату сверху от сегодня.
 */

export const PAGE_SIZE = 20;

export type PeriodFilter = 'week' | 'month' | 'all';

export const PERIOD_DAYS: Record<Exclude<PeriodFilter, 'all'>, number> = {
  week: 7,
  month: 30,
};

export const SORT_FIELDS = ['plannedDate', 'number', 'createdAt'] as const;

const DEFAULT_SORT: TableSort = { field: 'plannedDate', dir: 'asc' };

export interface PlanSearch {
  search?: string;
  period?: PeriodFilter;
  page?: number;
  sort?: string;
}

export interface ResolvedPlanSearch {
  search: string;
  period: PeriodFilter;
  page: number;
  sort: TableSort;
}

const isPeriodFilter = (value: unknown): value is PeriodFilter =>
  value === 'week' || value === 'month' || value === 'all';

export const validatePlanSearch = (
  raw: Record<string, unknown>,
): PlanSearch => {
  const result: PlanSearch = {};

  const search = typeof raw.search === 'string' ? raw.search.trim() : '';
  if (search !== '') result.search = search;

  if (isPeriodFilter(raw.period) && raw.period !== 'all')
    result.period = raw.period;

  const page = Number(raw.page);
  if (Number.isInteger(page) && page > 1) result.page = page;

  const sort = parseSort(raw.sort, SORT_FIELDS);
  if (sort && !isSameSort(sort, DEFAULT_SORT)) result.sort = formatSort(sort);

  return result;
};

export const resolvePlanSearch = (search: PlanSearch): ResolvedPlanSearch => ({
  search: search.search ?? '',
  period: search.period ?? 'all',
  page: search.page ?? 1,
  sort: parseSort(search.sort, SORT_FIELDS) ?? DEFAULT_SORT,
});

/** Местная дата через days дней в формате API (DateOnly): 2026-10-15. */
const dateAfter = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + days);

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

export const toPlanQuery = (
  resolved: ResolvedPlanSearch,
): NonNullable<GetApiShipmentsData['query']> => {
  const query: NonNullable<GetApiShipmentsData['query']> = {
    Statuses: ['Planned1'],
    PageNumber: resolved.page,
    PageSize: PAGE_SIZE,
    Sort: formatSort(resolved.sort),
  };

  if (resolved.search !== '') query.Search = resolved.search;

  // Верхняя граница у API исключительная: «неделя» - сегодня плюс 7 дней включительно.
  if (resolved.period !== 'all')
    query.PlannedDateTo = dateAfter(PERIOD_DAYS[resolved.period] + 1);

  return query;
};
